import {
  cloneSearchExpressionNode,
  compactSearchExpressionNode,
  normalizeSearchExpression,
  normalizeSearchExpressionNegated,
  normalizeSearchExpressionOperator,
} from "./core.js";
import { isPlainObject } from "./shared.js";
import {
  normalizePatchBound,
  normalizePatchId,
} from "./terms.js";

function assignPatchWindowBound(window, term) {
  const bound = normalizePatchBound(term?.bound);
  const patchId = normalizePatchId(term?.patchId);
  if (!bound || !patchId) {
    return false;
  }
  if (bound === "from") {
    window.fromPatchId = patchId;
    return true;
  }
  if (bound === "to") {
    window.toPatchId = patchId;
    return true;
  }
  return false;
}

function normalizePatchWindow(window) {
  const fromPatchId = normalizePatchId(window?.fromPatchId);
  const toPatchId = normalizePatchId(window?.toPatchId);
  if (!fromPatchId && !toPatchId) {
    return null;
  }
  return {
    ...(fromPatchId ? { fromPatchId } : {}),
    ...(toPatchId ? { toPatchId } : {}),
  };
}

export function patchWindowFromExpressionNode(node) {
  if (!isPlainObject(node) || normalizeSearchExpressionNegated(node.negated)) {
    return null;
  }
  const window = { fromPatchId: "", toPatchId: "" };
  const visit = (candidate) => {
    if (!isPlainObject(candidate) || normalizeSearchExpressionNegated(candidate.negated)) {
      return false;
    }
    if (candidate.type === "term") {
      return candidate.term?.kind === "patch-bound" && assignPatchWindowBound(window, candidate.term);
    }
    if (candidate.type !== "group" || normalizeSearchExpressionOperator(candidate.operator) !== "and") {
      return false;
    }
    const children = Array.isArray(candidate.children) ? candidate.children : [];
    return children.length > 0 && children.every((child) => visit(child));
  };
  return visit(node) ? normalizePatchWindow(window) : null;
}

function containsResolvedPatchBound(node) {
  if (!isPlainObject(node)) {
    return false;
  }
  if (node.type === "term") {
    return node.term?.kind === "patch-bound" && Boolean(normalizePatchId(node.term.patchId));
  }
  if (node.type === "container") {
    return containsResolvedPatchBound(node.expression);
  }
  return (Array.isArray(node.children) ? node.children : []).some((child) =>
    containsResolvedPatchBound(child)
  );
}

function normalizeFilterNode(operator, children, negated) {
  if (!children.length) {
    return null;
  }
  return compactSearchExpressionNode(
    {
      type: "group",
      operator,
      children,
      ...(negated ? { negated: true } : {}),
    },
    { isRoot: true },
  );
}

export function normalizeSearchQueryEnvelope(expression) {
  const normalized = normalizeSearchExpression(expression);
  if (!normalized) {
    return {
      filter: null,
      globalEvidenceWindow: null,
      hasScopedEvidenceWindows: false,
    };
  }

  const rootNegated = normalizeSearchExpressionNegated(normalized.negated);
  const rootOperator = normalizeSearchExpressionOperator(normalized.operator);
  const rootChildren = Array.isArray(normalized.children) ? normalized.children : [];
  const globalWindow = { fromPatchId: "", toPatchId: "" };
  let filter = cloneSearchExpressionNode(normalized);

  const canExtractDirectPatchChildren = !rootNegated && rootOperator === "and";
  if (canExtractDirectPatchChildren) {
    const remainingChildren = [];
    for (const child of rootChildren) {
      const childWindow = patchWindowFromExpressionNode(child);
      if (childWindow) {
        if (childWindow.fromPatchId) {
          globalWindow.fromPatchId = childWindow.fromPatchId;
        }
        if (childWindow.toPatchId) {
          globalWindow.toPatchId = childWindow.toPatchId;
        }
        continue;
      }
      remainingChildren.push(cloneSearchExpressionNode(child));
    }
    filter = normalizeFilterNode(rootOperator, remainingChildren, false);
  } else if (!rootNegated && rootChildren.length === 1) {
    const childWindow = patchWindowFromExpressionNode(rootChildren[0]);
    if (childWindow) {
      globalWindow.fromPatchId = childWindow.fromPatchId || "";
      globalWindow.toPatchId = childWindow.toPatchId || "";
      filter = null;
    }
  }

  const globalEvidenceWindow = normalizePatchWindow(globalWindow);
  return {
    filter,
    globalEvidenceWindow,
    hasScopedEvidenceWindows: containsResolvedPatchBound(filter),
  };
}
